import { GripVertical, X, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn, Checkbox, Input, Label } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import { formatShopPrice } from "../enduser/formatShopPrice";
import type { SelectedShopEntry } from "./types";

interface SelectedProductRowProps {
  entry: SelectedShopEntry;
  isVariantResolved: (stripePriceId: string) => boolean;
  isDragging: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onRemove: () => void;
  onBadgeChange: (badge: string | undefined) => void;
  onRecommendedChange: (recommended: boolean) => void;
  onCategoryChange: (category: string | undefined) => void;
}

export const SelectedProductRow = ({
  entry,
  isVariantResolved,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onRemove,
  onBadgeChange,
  onRecommendedChange,
  onCategoryChange,
}: SelectedProductRowProps) => {
  const { t } = useTranslation("shop");
  const unresolvedVariants = entry.variants.filter((v) => !isVariantResolved(v.stripePriceId));

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={cn("rounded-md border bg-background p-2 space-y-2 transition-opacity", isDragging && "opacity-50")}
    >
      <div className="flex items-center gap-2">
        <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded">
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{entry.title}</p>
          <p className="text-xs text-muted-foreground">
            {entry.variants.map((variant) => formatShopPrice(t, variant)).join(" · ")}
          </p>
        </div>
        <Button
          variant="ghost"
          iconOnly
          hoverLabel={t("Remove from shop")}
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {unresolvedVariants.length > 0 && (
        <div className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>
            {t(
              "This product isn't configured in the web app yet — add it there first, then it will be available here."
            )}
          </span>
        </div>
      )}

      <Input
        value={entry.category ?? ""}
        onChange={(e) => onCategoryChange(e.target.value || undefined)}
        placeholder={t('Category (e.g. "Hair Loss")')}
        className="text-xs"
      />

      <Input
        value={entry.badge ?? ""}
        onChange={(e) => onBadgeChange(e.target.value || undefined)}
        placeholder={t('Badge text (e.g. "Popular"; defaults to "Recommended for you")')}
        className="text-xs"
      />

      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
        <Checkbox checked={entry.recommended} onCheckedChange={(checked) => onRecommendedChange(checked === true)} />
        <Label className="cursor-pointer font-normal">{t("Show in the recommended rail")}</Label>
      </label>
    </div>
  );
};
